import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Circle, ExternalLink, LoaderCircle, Pause, Play } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import apiClient from '../services/apiClient';
import { t } from '../i18n';
import FlashCard from './FlashCard';
import LearningSessionHeader from './LearningSessionHeader';
import VocabularyPreview from './VocabularyPreview';
import VocabularySessionResult from './VocabularySessionResult';
import ScenePlayer from './ScenePlayer';
import useVocabularyEntryScene from '../hooks/useVocabularyEntryScene';
import useLearningCoaching from '../hooks/useLearningCoaching';
import LearningCoach from './LearningCoach';
import { createCoachingSelector, getVocabularyCardCoachingContext } from '../services/LearningCoaching';
import ConfirmDialog from './ConfirmDialog';
import { playPracticeSound, unlockPracticeAudio } from '../services/soundEffects';
import useVocabularyTimer, { VOCABULARY_LIMIT_MS, VOCABULARY_SCORING_RULE_VERSION, clearVocabularySessionCache } from '../hooks/useVocabularyTimer';
import { claimSpeechPlayback, registerSpeechPlayback, releaseSpeechPlayback } from '../services/SpeechPlaybackCoordinator';
import { getListeningAudio, unlockListeningAudio, invalidateListeningAudioUnlock } from '../services/ListeningAudioTransport';
import { buildVocabularyExampleSearchUrl } from '../utils/vocabularyExampleSearch';
import { lockBodyScroll } from '../utils/bodyScrollLock';
import VocabularyAudioCache from '../services/VocabularyAudioCache';
import { peekVocabularySession, discardVocabularySession, beginVocabularyLearning, revealVocabularyAnswer, advanceVocabularyCard } from '../services/VocabularyLearningSession';
import './VocabularyPage.css';
import './VocabularyPracticePage.css';

const v = (key, values) => t(`vocabulary.${key}`, undefined, values);
const currentRule = session => session?.scoringRuleVersion === VOCABULARY_SCORING_RULE_VERSION;
const DEPARTURE_FEEDBACK_MS = 260;
const CARD_FLIGHT_MS = 220;

export default function VocabularyPracticePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState(() => {
    const snapshot = peekVocabularySession(location.state?.vocabularySessionId);
    return currentRule(snapshot) ? snapshot : null;
  });
  const loading = false;
  const [fatal, setFatal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [faces, setFaces] = useState({});
  const [exitOpen, setExitOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [playing, setPlaying] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);
  const [audioError, setAudioError] = useState('');
  const [flipBusy, setFlipBusy] = useState(false);
  const [exitingPreview, setExitingPreview] = useState(false);
  const [departureFeedback, setDepartureFeedback] = useState(null);
  const [outgoingCard, setOutgoingCard] = useState(null);
  const entryScene = useVocabularyEntryScene(session, !loading && !error);
  const faceSide = useRef('front');
  const audioCache = useRef(null);
  if (!audioCache.current) audioCache.current = new VocabularyAudioCache((...args) => apiClient.fetchTtsAudioBlob(...args));
  const autoAudioKey = useRef(null);
  const playAudioRef = useRef(null);
  const alive = useRef(false);
  const abandoned = useRef(false);
  const learningContentRef = useRef(null);
  const saveHeadingRef = useRef(null);
  useEffect(() => { if (session?.status === 'ready_to_complete') saveHeadingRef.current?.focus(); }, [session?.status]);
  const previousPhaseRef = useRef(session?.phase);
  useEffect(() => {
    if (previousPhaseRef.current === 'preview' && session?.phase === 'learning') learningContentRef.current?.focus();
    previousPhaseRef.current = session?.phase;
  }, [session?.phase]);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const pending = useRef(null);
  const inFlight = useRef(false);
  const audioRef = useRef(null);
  const audioUrl = useRef(null);
  const audioGeneration = useRef(0);
  const playbackOwner = useRef(`vocabulary-${uuid()}`);
  const previewing = session?.phase === 'preview' && (session?.status === 'active' || exitingPreview);
  const isCardPractice = !entryScene.blocked && !previewing && session?.status === 'active' && Boolean(session?.current);
  const { scenes: coachingScenes } = useLearningCoaching('vocabulary', { enabled: isCardPractice, entryKey: session?.id || '' });
  const coachingSelector = useRef(null);
  if (!coachingSelector.current) coachingSelector.current = createCoachingSelector();
  useEffect(() => isCardPractice ? lockBodyScroll() : undefined, [isCardPractice]);
  const paused = entryScene.blocked || previewing || busy || flipBusy || exitOpen || loading || Boolean(error);
  const timer = useVocabularyTimer(session, { paused });
  const timerRef = useRef(timer);
  timerRef.current = timer;
  const stopAudio = useCallback(() => {
    audioGeneration.current += 1;
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.onended = null; audio.onerror = null;
      audio.pause(); audio.removeAttribute('src');
    }
    if (audioUrl.current) URL.revokeObjectURL(audioUrl.current);
    audioUrl.current = null;
    releaseSpeechPlayback(playbackOwner.current);
    if (alive.current) { setPlaying(false); setAudioBusy(false); }
  }, []);
  useEffect(() => registerSpeechPlayback(playbackOwner.current, stopAudio), [stopAudio]);
  useEffect(() => {
    const invalidate = () => {
      abandoned.current = true;
      discardVocabularySession(sessionRef.current?.id);
      sessionRef.current = null;
      timerRef.current.discard();
      stopAudio();
      setSession(null);
    };
    const onShow = () => { if (!sessionRef.current) navigate('/vocabulary', { replace: true }); };
    window.addEventListener('pagehide', invalidate);
    window.addEventListener('pageshow', onShow);
    return () => {
      window.removeEventListener('pagehide', invalidate);
      window.removeEventListener('pageshow', onShow);
    };
  }, [navigate, stopAudio]);

  const adopt = useCallback(next => {
    if (!alive.current || !currentRule(next)) return false;
    sessionRef.current = next;
    setSession(next);
    return true;
  }, []);
  useEffect(() => {
    alive.current = true;
    clearVocabularySessionCache();
    if (!sessionRef.current) navigate('/vocabulary', { replace: true });
    return () => {
      alive.current = false; stopAudio(); audioCache.current.clear();
      discardVocabularySession(sessionRef.current?.id);
    };
  }, [navigate, stopAudio]);
  useEffect(() => {
    if (!loading && session?.status === 'active') audioCache.current.update(session.id, session.audioPreload || []);
    else audioCache.current.clear();
  }, [loading, session]);
  useEffect(() => {
    if (!outgoingCard) return undefined;
    const timeout = setTimeout(() => setOutgoingCard(null), CARD_FLIGHT_MS);
    return () => clearTimeout(timeout);
  }, [outgoingCard]);
  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(() => setToast(''), 1300);
    return () => clearTimeout(timeout);
  }, [toast]);
  useEffect(() => {
    if (!departureFeedback) return undefined;
    const timeout = setTimeout(() => setDepartureFeedback(null), DEPARTURE_FEEDBACK_MS);
    return () => clearTimeout(timeout);
  }, [departureFeedback]);
  const appearance = session?.current;
  const direction = appearance?.direction || session?.direction || 'q_to_a';
  const appearanceId = appearance?.id;
  useEffect(() => {
    stopAudio(); setAudioError(''); setFlipBusy(false);
    learningContentRef.current?.querySelectorAll('.flashcard-content').forEach(element => { element.scrollTop = 0; });
  }, [appearanceId, stopAudio]);
  const displayedSide = faces[appearanceId] || 'front';
  faceSide.current = displayedSide;
  const setFace = useCallback(side => {
    setFlipBusy(false);
    if (appearanceId) setFaces(current => ({ ...current, [appearanceId]: side }));
  }, [appearanceId]);

  const execute = useCallback(async operation => {
    if (inFlight.current) return null;
    inFlight.current = true; pending.current = operation; setBusy(true); setError(''); setFatal(false);
    try {
      const next = operation.type === 'complete'
        ? await apiClient.completeVocabularySession(operation.sessionId, operation.payload)
        : await apiClient.vocabularyAction(operation.sessionId, 'preview_exclude', operation.payload);
      pending.current = null;
      if (!alive.current || abandoned.current) return next;
      if (operation.type === 'preview_exclude') {
        if (next.status === 'ended') setExitingPreview(true);
        setToast(v('learned'));
      }
      adopt(next);
      return next;
    } catch (err) {
      if (alive.current && !abandoned.current) {
        setError(err.message || v('networkFailed'));
        setFatal([400, 401, 403, 404, 409].includes(err.status || err.statusCode));
      }
      return null;
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  }, [adopt]);

  const action = useCallback((type, extra = {}) => {
    const current = sessionRef.current;
    if (inFlight.current || pending.current || !current || current.status !== 'active') return Promise.resolve(null);
    if (type === 'preview_exclude') {
      if (current.phase !== 'preview') return Promise.resolve(null);
      return execute({ type, sessionId: current.id,
        payload: { operationId: uuid(), revision: current.revision, cardIds: extra.cardIds } });
    }
    if (extra.appearanceId && extra.appearanceId !== current.current?.id) return Promise.resolve(null);
    let next;
    if (type === 'start_learning') {
      next = beginVocabularyLearning(current);
    } else if (type === 'reveal') {
      next = revealVocabularyAnswer(current, current.current?.id);
    } else if (type === 'advance') {
      const correct = !current.current.revealed;
      const activeCard = learningContentRef.current?.querySelector('.vocabulary-card-stage > .flashcard-container > .flashcard');
      const dragY = Number.parseFloat(activeCard?.style.getPropertyValue('--flashcard-drag-y')) || 0;
      stopAudio();
      setOutgoingCard({ appearance: current.current, side: faceSide.current, direction: current.current.direction || current.direction,
        answerRevealed: Boolean(current.cardStates[current.current.cardId]?.revealed), dragY });
      setDepartureFeedback({ id: current.current.id, correct, points: 2 });
      void playPracticeSound(correct ? 'answerOk' : 'cardFlip', { recoverOnGesture: true });
      next = advanceVocabularyCard(current, current.current.id);
    } else return Promise.resolve(null);
    adopt(next);
    return Promise.resolve(next);
  }, [adopt, execute, stopAudio]);

  const finish = useCallback(() => {
    if (inFlight.current) return;
    abandoned.current = true;
    stopAudio(); timerRef.current.discard(); pending.current = null;
    discardVocabularySession(sessionRef.current?.id);
    sessionRef.current = null;
    navigate('/vocabulary', { replace: true });
  }, [navigate, stopAudio]);
  useEffect(() => {
    if (session?.status === 'ready_to_complete' && !pending.current && !error) {
      const payload = { operationId: uuid(), revision: session.revision,
        attempts: session.attempts, activeMs: timerRef.current.snapshot().elapsedMs };
      void execute({ type: 'complete', sessionId: session.id, payload });
    }
  }, [session, error, execute]);
  useEffect(() => {
    if (session?.status === 'active' && timer.elapsedMs >= VOCABULARY_LIMIT_MS && !paused && !pending.current) finish();
  }, [session?.status, timer.elapsedMs, paused, finish]);

  const next = () => {
    if (paused || pending.current) return false;
    stopAudio();
    void unlockPracticeAudio({ recoverOnGesture: true });
    void unlockListeningAudio();
    return action('advance', { appearanceId });
  };
  const reveal = ({ to }) => {
    setFlipBusy(true);
    void unlockListeningAudio();
    void playPracticeSound('cardFlip', { recoverOnGesture: true });
    if (to !== 'back' || session?.current?.revealed) return;
    void action('reveal', { appearanceId });
  };
  const playAudio = async ({ automatic = false } = {}) => {
    if (paused || pending.current || !appearance) return;
    if (playing && !automatic) { stopAudio(); return; }
    stopAudio();
    // Must happen in the tap/keyboard handler, before reveal/network awaits.
    // Safari authorizes this reusable media element, not the Web Audio effects.
    if (!automatic) void unlockListeningAudio();
    setAudioBusy(true); setAudioError('');
    const generation = ++audioGeneration.current;
    try {
      if (direction === 'q_to_a' && !appearance.revealed) {
        // Hearing the learning-language answer in Q → A exposes it too.
        setFace(displayedSide);
        const revealed = await action('reveal', { appearanceId: appearance.id });
        if (!revealed) return;
      }
      const prefetched = audioCache.current.get(session.id, appearance.cardId);
      let blob;
      if (prefetched) { try { blob = await prefetched; } catch { /* Fall back to the existing eligible appearance path. */ } }
      if (!alive.current || generation !== audioGeneration.current) return;
      if (!blob) {
        const info = session.audioPreload.find(item => item.cardId === appearance.cardId);
        blob = await apiClient.fetchTtsAudioBlob(info.audioUrl);
      }
      if (!alive.current || generation !== audioGeneration.current) return;
      audioUrl.current = URL.createObjectURL(blob);
      claimSpeechPlayback(playbackOwner.current);
      const audio = getListeningAudio();
      audioRef.current = audio;
      audio.src = audioUrl.current;
      audio.onended = () => { if (generation === audioGeneration.current) stopAudio(); };
      audio.onerror = () => { if (alive.current && generation === audioGeneration.current) { stopAudio(); setAudioError(v('audioFailed')); } };
      await audio.play();
      if (alive.current && generation === audioGeneration.current) setPlaying(true);
    } catch (err) {
      if (alive.current && generation === audioGeneration.current) {
        if (err.name === 'NotAllowedError') invalidateListeningAudioUnlock(audioRef.current);
        setAudioError(err.name === 'NotAllowedError' ? v('autoplayBlocked') : err.message || v('audioFailed')); stopAudio();
      }
    }
    finally { if (alive.current && generation === audioGeneration.current) setAudioBusy(false); }
  };
  playAudioRef.current = playAudio;
  useEffect(() => {
    if (direction === 'q_to_a' && displayedSide !== 'back') autoAudioKey.current = null;
    const autoplay = () => {
      if (document.hidden || paused || pending.current || !appearance?.presented || session?.status !== 'active' ||
        (direction === 'q_to_a' && (displayedSide !== 'back' || !appearance.revealed))) return;
      const key = `${session.id}:${appearance.id}`;
      if (autoAudioKey.current === key) return;
      autoAudioKey.current = key;
      void playAudioRef.current({ automatic: true });
    };
    autoplay();
    document.addEventListener('visibilitychange', autoplay);
    return () => document.removeEventListener('visibilitychange', autoplay);
  }, [appearance, displayedSide, direction, paused, session?.id, session?.status]);
  const card = useMemo(() => appearance ? { id: appearance.id, ordinal: appearance.ordinal, grade: appearance.grade, front: appearance.Q, back: appearance.A, backMemo: appearance.backMemo, cardType: 'normal', level: 1 } : null, [appearance]);
  const controlsDisabled = paused || Boolean(pending.current) || session?.status !== 'active' || !session?.current?.presented;
  const exampleSearchUrl = useMemo(() => buildVocabularyExampleSearchUrl(appearance?.A), [appearance?.A]);
  const showExampleSearch = displayedSide === 'back' && appearance?.revealed && Boolean(exampleSearchUrl);
  const canSearchExamples = showExampleSearch && !controlsDisabled;
  const handleExampleSearch = event => {
    if (event.type === 'auxclick' && event.button !== 1) return;
    if (!canSearchExamples) { event.preventDefault(); return; }
    stopAudio();
  };
  const retry = () => { if (pending.current && !fatal) void execute(pending.current); };
  const progressMax = session?.batchProgress?.max || 0;
  const progressValue = session?.batchProgress?.value || 0;
  const progressRatio = progressMax ? progressValue / progressMax : 0;
  const vocabularyCoaching = getVocabularyCardCoachingContext({ session, isBack: displayedSide === 'back' });
  const vocabularyCoachingTrigger = coachingScenes.some(scene => scene?.trigger === vocabularyCoaching?.trigger)
    ? vocabularyCoaching.trigger : 'vocabulary.card.show';
  if (!loading && !error && entryScene.blocked) return entryScene.scene
    ? <ScenePlayer scene={entryScene.scene} onContinue={() => { void unlockListeningAudio(); entryScene.complete(); }} continueLabel={v('startLearning')} />
    : <main className="vocabulary-practice-page"><p role="status">{v('loading')}</p></main>;
  if (session?.status === 'ready_to_complete') return <main className="vocabulary-practice-page">
    <h1 ref={saveHeadingRef} tabIndex={-1}>{v('savingResults')}</h1>
    {busy && <p role="status">{v('savingResultsMessage')}</p>}
    {error && <div className="vocabulary-error" role="alert"><p>{error}</p>
      {!fatal && <button type="button" onClick={retry} disabled={busy}>{v('retry')}</button>}
    </div>}
    <button type="button" className="vocabulary-primary" onClick={finish} disabled={busy}>{v('backBooks')}</button>
  </main>;
  if (!loading && session?.status === 'ended' && !exitingPreview) return <main className="vocabulary-practice-page">
    {error && <div className="vocabulary-error" role="alert"><p>{error}</p><button type="button" onClick={fatal ? finish : retry} disabled={busy}>{v(fatal ? 'backBooks' : 'retry')}</button></div>}
    <VocabularySessionResult session={session} onBack={() => navigate('/vocabulary')} />
  </main>;
  return <main className={`vocabulary-practice-page${isCardPractice ? ' is-card-practice' : ''}`}>
    <div className="vocabulary-practice-content" ref={learningContentRef} tabIndex={-1} aria-hidden={exitOpen ? true : undefined} inert={exitOpen ? '' : undefined}>
      <LearningSessionHeader title={<h1>{previewing ? v('previewTitle') : appearance?.noteTitle || session?.note?.title || v('title')}</h1>} elapsedSeconds={timer.elapsedSeconds} timeLimitSeconds={1800}
        exitLabel={previewing ? v('backBooks') : v('end')} exitDisabled={busy || exitingPreview} onExit={() => { stopAudio(); if (previewing || session?.status === 'ended') finish(); else setExitOpen(true); }}
        leftTools={!previewing && <div className="vocabulary-audio-control"><button type="button" className="vocabulary-audio-button" onClick={playAudio} disabled={!appearance || controlsDisabled || audioBusy} aria-label={v(playing ? 'pause' : 'play')} title={v(playing ? 'pause' : 'play')}>{audioBusy ? <LoaderCircle size={22} className="vocabulary-loading-icon" /> : playing ? <Pause size={22} /> : <Play size={22} />}</button>{audioError && <span role="alert">{audioError}</span>}</div>}
        />
      {!loading && isCardPractice && <div className="vocabulary-session-progress" role="progressbar" aria-label={v('sessionProgress')}
        aria-valuemin={0} aria-valuemax={Math.max(1, progressMax)} aria-valuenow={progressValue}
        aria-valuetext={v('progressPercent', { percent: Math.floor(progressRatio * 100) })}>
        <span aria-hidden="true" style={{ transform: `scaleX(${progressRatio})` }} />
      </div>}
      {loading && <p role="status">{v('loading')}</p>}
      {error && <div className="vocabulary-error" role="alert"><p>{error}</p><button type="button" onClick={fatal ? finish : retry} disabled={busy}>{v(fatal ? 'backBooks' : 'retry')}</button></div>}
      {toast && <p className="vocabulary-toast" role="status">{toast}</p>}
      {!loading && previewing && <VocabularyPreview cards={session.preview?.cards || []} targetCount={session.preview?.targetCount || 10}
        direction={session.direction} disabled={busy || Boolean(pending.current) || Boolean(error) || exitOpen}
        onExclude={cardId => { void action('preview_exclude', { cardIds: [cardId] }); }}
        onEmptySettled={() => { setExitingPreview(false); if (session.status === 'ended') finish(); }}
        onStart={() => { void unlockPracticeAudio({ recoverOnGesture: true }); void unlockListeningAudio(); void action('start_learning'); }} />}
      {appearance && card && <section className="vocabulary-learning-content">
        {isCardPractice && <LearningCoach scenes={coachingScenes} selectScene={coachingSelector.current} trigger={vocabularyCoachingTrigger} visitKey={session.id}
          variables={vocabularyCoaching.variables} />}
        <div className="vocabulary-card-workout">
        <div className="vocabulary-card-stage">
        {outgoingCard && <div key={outgoingCard.appearance.id} className="vocabulary-card-outgoing" aria-hidden="true" inert=""
          style={{ '--vocabulary-exit-start-y': `${outgoingCard.dragY}px` }}
          onAnimationEnd={event => {
            if (event.target === event.currentTarget) setOutgoingCard(current => current?.appearance.id === outgoingCard.appearance.id ? null : current);
          }}>
          <FlashCard card={{ id: outgoingCard.appearance.id, ordinal: outgoingCard.appearance.ordinal, grade: outgoingCard.appearance.grade, front: outgoingCard.appearance.Q, back: outgoingCard.appearance.A, backMemo: outgoingCard.appearance.backMemo, cardType: 'normal', level: 1 }}
            interactionMode="vocabulary" vocabularyAnswerRevealed={outgoingCard.answerRevealed}
            reverseMode={outgoingCard.direction === 'a_to_q'} displayedSide={outgoingCard.side} keyboardShortcutsEnabled={false} interactionDisabled answerChoiceAvailable={false} />
        </div>}
        <FlashCard key={appearance.id} card={card} interactionMode="vocabulary" reverseMode={direction === 'a_to_q'}
          vocabularyAnswerRevealed={Boolean(session.cardStates?.[appearance.cardId]?.revealed)}
          displayedSide={displayedSide} onDisplayedSideChange={setFace} onReveal={reveal}
          onSwipeUp={next} keyboardShortcutsEnabled={!controlsDisabled}
          interactionDisabled={controlsDisabled} answerChoiceAvailable={false} />
        {departureFeedback && <div className="vocabulary-departure-feedback" role="status" aria-live="polite" aria-atomic="true"
          aria-label={departureFeedback.correct ? t('practiceUi.encounter.remembered') : v('presentationBonus')}>
          {departureFeedback.correct && <Circle size={36} strokeWidth={3} aria-hidden="true" />}
          {departureFeedback.points > 0 && <span>+{departureFeedback.points} pt</span>}
        </div>}
        </div>
        {exampleSearchUrl && <div className="vocabulary-card-tools">
          {showExampleSearch && <a className="vocabulary-example-search" href={exampleSearchUrl} target="_blank" rel="noopener noreferrer"
            onClick={handleExampleSearch} onAuxClick={handleExampleSearch} aria-disabled={controlsDisabled || undefined} tabIndex={controlsDisabled ? -1 : undefined}
            aria-label={v('exampleSearchNewTab')} title={v('exampleSearchNewTab')}>
            {v('exampleSearch')}<ExternalLink size={16} aria-hidden="true" />
          </a>}
        </div>}
        </div>
      </section>}
      {!loading && session?.status === 'active' && !previewing && !session.current && <section className="vocabulary-end" role="status"><h2>{v('waiting')}</h2><p>{session.availability?.nextEligibleDay ? v('waitingUntil', { date: session.availability.nextEligibleDay }) : v('saved')}</p><button type="button" className="vocabulary-primary" onClick={() => navigate('/vocabulary')}>{v('backBooks')}</button></section>}
    </div>
    <ConfirmDialog isOpen={exitOpen} title={v('endTitle')} message={v('endMessage')} confirmLabel={v('end')} onConfirm={finish} onCancel={() => setExitOpen(false)} />

  </main>;
}
